/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Box, Divider, Link, styled } from '@mui/material';
import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';

import { colors } from '@farmlands/mui-theme';
import { ImageSlider } from '../../../../components/ImageSlider';
import Typography from '../../../../components/Typography';
import {
  MediaLocationType,
  Product,
  ProductDetailsApiResponse,
  Variant,
  getStockAvailabilities,
} from '../../storage';
import { ProductPriceDetails } from '../ProductCard/ProductPriceDetails';
import TestIds from '../../../../testIds';
import { ProductVariantDetails } from '../ProductCard/ProductVariantDetails';
import Counter from '../../../../components/Counter';
import { Button } from '../../../../components/Button';
import RequestBulkHelperMessage from '../../../quotes/components/HelperMessage/RequestBulkHelperMessage';
import { useQuoteModal } from '../../../quotes/components/QuoteModal/useQuoteModal';
import { selectCanUseQuickOrder, selectCurrentFarmerAccount } from '../../../azure-b2c/storage';
import { getUpdatedVariant } from '../../../cart/utils/getUpdatedVariant';
import { CartItem } from '../../../cart/storage/cart.types';
import { useAppDispatch } from '../../../../store/rootStore';
import CartSlice from '../../../cart/storage/cart.slice';
import MaterialSymbol from '../../../../components/MaterialSymbol';
import { getProductPrices } from '../../utils/getPrices';
import { URLS } from '../../../../util/consts';
import { StockAvailability } from '../StockAvailability/StockAvailability';
import { StocksSlice, selectProduct } from '../../storage/stocks.slice';
import { CostPriceList } from '../../../cost-price/components/CostPriceList';
import { useBranch } from '../../../../util/useBranch';
import { useProductStatus } from '../../utils/useProductStatus';
import { QuoteModalType } from '../../../quotes/components/QuoteModal/types';

const InfoContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  background: colors.shade0,
  padding: '32px',
  gap: '24px',
  border: `1px solid ${colors.primary100}`,
  marginTop: '24px',
  width: '100%',
  [theme.breakpoints.down('lg')]: {
    flexDirection: 'column',
    padding: '16px',
    minWidth: 'unset',
  },
}));

const FlexRowBox = styled(Box)({
  display: 'flex',
  width: '100%',
});

const FlexColBox = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
});

const HelperMessageContainer = styled('div')(({ theme }) => ({
  background: colors.primary100,
  [theme.breakpoints.down('lg')]: {
    margin: '0 -16px -16px -16px',
  },
}));

export interface QueryVariant {
  size: string;
  colour: string;
  qty: number;
}

export const safeQuantity = (value: string | null) => {
  return parseInt(value ?? '1');
};
interface ProductInfoProps {
  item: ProductDetailsApiResponse;
  queryVariant: QueryVariant;
  setQueryVariant: React.Dispatch<React.SetStateAction<QueryVariant>>;
}

export const ProductInfo = ({ item, queryVariant, setQueryVariant }: ProductInfoProps) => {
  const dispatch = useAppDispatch();
  const selectedAccount = useSelector(selectCurrentFarmerAccount);
  const [currentProductId, setCurrentProductId] = useState(item.productId || item.masterProductId);

  const product = useSelector(selectProduct(currentProductId));
  const isTfo = selectedAccount?.hasTfoAccess;
  const quoteProduct = useQuoteModal();
  const canUseCart = useSelector(selectCanUseQuickOrder);
  const branchId = useBranch();

  React.useEffect(() => {
    const cartItem = {
      ...item,
      productId: currentProductId,
      quantity: queryVariant.qty,
      description: item.name,
      imageUrl:
        item.mediaLocations && item.mediaLocations.length > 0
          ? transformImageUrl(item.mediaLocations)[0]
          : '',
    } as CartItem;

    dispatch(StocksSlice.actions.addStock(cartItem));

    const getStocks = async () => {
      const items = await getStockAvailabilities({
        productIds:
          item.variants?.map((v) => v.productId).length === 0
            ? [item.masterProductId]
            : item.variants?.map((v) => v.productId),
      });
      if (items && items.length > 0) dispatch(StocksSlice.actions.addAvailabilities(items));
    };

    getStocks();
  }, [item]);

  const onChangeVariant = useCallback((variant: Variant) => {
    const updatedVariant = getUpdatedVariant(variant, {
      ...product,
      quantity: queryVariant.qty,
    } as CartItem);
    setCurrentProductId(updatedVariant.productId);
    dispatch(StocksSlice.actions.addStock(updatedVariant));
    setQueryVariant({
      ...queryVariant,
      size: updatedVariant.sizeDescription ?? '',
      colour: updatedVariant.colourDescription ?? '',
    });
  }, []);

  const handleAddToCart = () => {
    if (product && selectedAccount) {
      dispatch(
        CartSlice.actions.addToCart({
          accountNumber: selectedAccount.accountNumber,
          product: {
            ...product,
            quantity: queryVariant.qty,
            description: item.name,
            basePriceExcGst: item.basePriceExcGst,
            basePriceIncGst: item.basePriceIncGst,
            discountedPriceExcGst: item.discountedPriceExcGst,
            discountedPriceIncGst: item.discountedPriceIncGst,
          },
        }),
      );
      toast('Item added to cart', {
        type: 'success',
        icon: <MaterialSymbol>check_circle</MaterialSymbol>,
      });
    }
  };

  const transformImageUrl = (images: MediaLocationType[]) => {
    return images?.map((x) => `${URLS.imageBase}${x.uri}`);
  };

  const renderBasicInfo = React.useMemo(() => {
    return (
      <FlexColBox gap={1} data-testid={TestIds.feat.product.details.descriptionContainer}>
        <Typography variant='h2' color='grey800' data-testid={TestIds.feat.product.card.name}>
          {item.name}
        </Typography>
        <Typography variant='body2' color='grey700' data-testid={TestIds.feat.product.card.SKU}>
          {`SKU: ${item.itemNumber}`}
        </Typography>
        <StockAvailability productId={currentProductId} showStore />
        <Divider sx={{ mt: 0.5, mb: 0.5 }} />
        <Typography
          variant='body2'
          color='grey700'
          data-testid={TestIds.feat.product.details.shortDescription}
        >
          {item.onlineShortDescription}
        </Typography>
      </FlexColBox>
    );
  }, [
    item.name,
    item.itemNumber,
    item.onlineShortDescription,
    product,
    currentProductId,
    branchId,
  ]);

  const renderPrices = React.useMemo(() => {
    return <ProductPriceDetails items={getProductPrices({ item })} size='Large' />;
  }, [item.discountedPriceExcGst, item.basePriceExcGst]);

  const { isDisabled, isEndOfLife } = useProductStatus(item.lifeCycleState, item.basePriceExcGst);

  const renderVariants = React.useMemo(() => {
    if (product && item.variants && item.variants.length > 0)
      return (
        <ProductVariantDetails
          key={'variants'}
          item={product}
          enableVariants
          onChangeVariant={onChangeVariant}
        />
      );
  }, [product, item.variants, onChangeVariant, isDisabled, isEndOfLife]);

  const renderButtons = React.useMemo(() => {
    if (canUseCart) {
      if (!isTfo && product?.stockAvailability === 'Enquire') {
        return (
          <FlexRowBox gap={2}>
            <Button
              key='enquire-button'
              variant='contained'
              onClick={() => {
                quoteProduct.setQuoteType(QuoteModalType.ENQUIRY);
                quoteProduct.setProduct(product);
              }}
              sx={{ mt: 'auto', transition: 'all 0.5s ease', whiteSpace: 'nowrap', width: '100%' }}
              data-testid={TestIds.feat.product.details.enquireButton}
            >
              Enquire
            </Button>
          </FlexRowBox>
        );
      }

      return (
        <FlexRowBox gap={2}>
          <Counter
            value={queryVariant.qty}
            onChange={(value) => {
              if (value) {
                setQueryVariant({
                  ...queryVariant,
                  qty: value,
                });
              }
            }}
          />
          <Button
            key='add-to-cart-button'
            variant='contained'
            onClick={handleAddToCart}
            sx={{ mt: 'auto', transition: 'all 0.5s ease', whiteSpace: 'nowrap', width: '100%' }}
            data-testid={TestIds.feat.product.details.orderButton}
          >
            Add to cart
          </Button>
        </FlexRowBox>
      );
    }
    return null;
  }, [product, queryVariant, canUseCart]);

  const renderHelpMessage = React.useMemo(() => {
    if (item.isPriceNegotiable && quoteProduct.canRequestQuote && !isTfo) {
      return (
        <HelperMessageContainer>
          <RequestBulkHelperMessage show />
          <Link
            component='button'
            onClick={() => {
              quoteProduct.setQuoteType(QuoteModalType.QUOTE_SINGLE);
              quoteProduct.setProduct(product as Product);
            }}
            sx={{ mb: 1, ml: 2, p: 0 }}
            data-testid={TestIds.feat.product.details.requestQuoteButton}
          >
            <Typography variant='body2' color='primary700'>
              Request a quote
            </Typography>
          </Link>
        </HelperMessageContainer>
      );
    }
  }, [product]);

  const ImageSliderMemo = React.useMemo(() => {
    return <ImageSlider images={transformImageUrl(item.mediaLocations ?? [])} />;
  }, [item.mediaLocations]);
  return (
    <InfoContainer>
      <FlexRowBox flex={6} justifyContent={'center'}>
        {ImageSliderMemo}
      </FlexRowBox>
      <FlexColBox flex={4} gap={4} data-testid={TestIds.feat.product.details.container}>
        {renderBasicInfo}
        {renderPrices}
        {renderVariants}
        {renderButtons}
        {renderHelpMessage}
        <CostPriceList productId={currentProductId} />
      </FlexColBox>
    </InfoContainer>
  );
};
